import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

export function RequestFilter() {
  const nav = useNavigate({ from: "/app/performance/requests" });
  const search = useSearch({
    from: "/app/performance/requests",
  }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const titleFilter = findCondition(filters, "title");
  const statusFilter = findCondition(filters, "status");
  const channelFilter = findCondition(filters, "channel");
  const [searchValue, setSearchValue] = useState<string>(
    (titleFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((titleFilter?.value as string) || ""),
    [titleFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "title",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "title");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const statusValue =
    typeof statusFilter?.value === "string" ? statusFilter.value : "";
  const channelValue =
    typeof channelFilter?.value === "string" ? channelFilter.value : "";
  const hasFilter = !!titleFilter?.value || !!statusValue || !!channelValue;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຄຳຂໍ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
          />
          <SimpleSelect
            value={statusValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "status");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "status",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທຸກສະຖານະ" },
              { value: "new", label: "ໃໝ່" },
              { value: "negotiating", label: "ກຳລັງເຈລະຈາ" },
              { value: "pending_quote", label: "ລໍຖ້າໃບສະເໜີລາຄາ" },
              { value: "won", label: "ຢືນຢັນແລ້ວ" },
              { value: "lost", label: "ເສຍໂອກາດ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />
          <SimpleSelect
            value={channelValue || "all"}
            onValueChange={(val) => {
              let nextFilters: FilterConditionDTO[] | undefined = filters;
              if (val === "all") {
                nextFilters = removeConditions(filters, "channel");
              } else {
                nextFilters = upsertCondition(filters, {
                  field: "channel",
                  op: "eq",
                  value: val,
                });
              }
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: nextFilters?.length ? nextFilters : undefined,
                },
              });
            }}
            placeholder="ຊ່ອງທາງ"
            className="h-8 w-full sm:w-36"
            options={[
              { value: "all", label: "ທຸກຊ່ອງທາງ" },
              { value: "phone", label: "ໂທລະສັບ" },
              { value: "facebook", label: "Facebook" },
              { value: "walk_in", label: "ເຂົ້າມາຕິດຕໍ່" },
              { value: "referral", label: "ແນະນຳ" },
              { value: "other", label: "ອື່ນໆ" },
            ]}
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({ search: { ...search, filters: undefined, offset: 0 } })
            }
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
